
import { cn } from "@/lib/utils";
import { useState, useEffect, useRef } from "react";
import { animationCoordinator, AnimationStage } from "@/lib/managers/AnimationCoordinator";

interface AnswerRowProps {
  word: string;
  show: boolean;
  onAnimationComplete?: () => void;
}

export const AnswerRow = ({ word, show, onAnimationComplete }: AnswerRowProps) => {
  const [revealedLetters, setRevealedLetters] = useState<boolean[]>([]);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const animationIdRef = useRef<string>(`answer-${word}-${Date.now()}`);
  const isAnimatingRef = useRef<boolean>(false);
  
  // Check for small screen
  useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth <= 375);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  
  // Reset animation state when word changes
  useEffect(() => {
    animationCoordinator.cancelAnimation(animationIdRef.current);
    animationIdRef.current = `answer-${word}-${Date.now()}`;
    setRevealedLetters(Array(word?.length || 0).fill(false));
    isAnimatingRef.current = false;
  }, [word]);
  
  // Start or cancel animation based on show prop
  useEffect(() => {
    // Don't do anything if we don't have a word
    if (!word) return;
    
    if (show && !isAnimatingRef.current) {
      console.log('AnswerRow: Registering new animation for sequential reveal');
      isAnimatingRef.current = true;
      
      // Register this animation
      animationCoordinator.registerAnimation(
        animationIdRef.current,
        word.length,
        onAnimationComplete
      );
      
      // Start with all letters hidden
      setRevealedLetters(Array(word.length).fill(false));
      
      // Set up letter reveal sequence with controlled timing
      const baseDelay = 150;
      
      for (let i = 0; i < word.length; i++) {
        const revealTimer = window.setTimeout(() => {
          // Update local state for rendering
          setRevealedLetters(prev => {
            const updated = [...prev];
            updated[i] = true;
            return updated;
          });
          
          // Update centrally managed state
          animationCoordinator.revealLetter(animationIdRef.current, i);
        }, i * baseDelay);
      }
    } else if (!show && isAnimatingRef.current) {
      // Cancel animation when show becomes false
      animationCoordinator.cancelAnimation(animationIdRef.current);
      isAnimatingRef.current = false;
      setRevealedLetters(Array(word.length).fill(false));
    }
    
    // Cleanup function
    return () => {
      if (isAnimatingRef.current) {
        animationCoordinator.cancelAnimation(animationIdRef.current);
        isAnimatingRef.current = false;
      }
    };
  }, [show, word, onAnimationComplete]);

  if (!word) return null;
  
  return (
    <div className="flex justify-center w-full absolute top-0 left-0">
      <div 
        className="letter-grid grid absolute z-[50]"
        style={{ 
          gridTemplateColumns: `repeat(${word.length}, 1fr)`,
          gap: isSmallScreen ? '1px' : '2px'
        }}
        data-answer-row="true"
      >
        {Array.from(word).map((letter, index) => (
          <div
            key={index}
            className="answer-letter-wrapper"
            data-answer="true"
            data-index={index}
            data-revealed={revealedLetters[index] ? "true" : "false"}
            style={{
              width: isSmallScreen ? '30px' : '40px', 
              height: isSmallScreen ? '30px' : '40px'
            }}
          >
            <div 
              className={cn(
                "answer-letter", 
                revealedLetters[index] ? "revealed" : ""
              )}
            >
              <div className="letter-front"></div>
              <div className="letter-back">
                <span className="letter-content">{letter.toUpperCase()}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
